// MCIP-21 — QA-manager pod overview (design board surface 1, beta).
// Persona question, stated per board rule: "which pod needs me today?"
// One card per pod for a manager with 90 seconds: pass-rate sparkline,
// signals split by classification colour, incident badge, top-signal teaser.
// Cards are pre-sorted by the API — incidents first, then open signals.
// Quiet pods stay small: no invented urgency.
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { pct, signedPts, compact } from '../../lib/format';
import { chipCounts, sparkPath, podState } from '../../lib/pods';
import { seriesOne } from '../../lib/palette';
import SharedTopBar from '../shared/TopBar';
import { ClassificationChip } from '../shared/Chip';
import SliceMatrix from './SliceMatrix';

const SPARK_W = 200;
const SPARK_H = 30;

function PodCard({ pod, mode, onOpenPod, hasJobs }) {
  const state = podState(pod);
  const chips = chipCounts(pod.signals.by_classification);
  const path = sparkPath(pod.spark, { width: SPARK_W, height: SPARK_H });
  const hasDelta = pod.delta_pts !== null && pod.delta_pts !== undefined;

  // The WHOLE card is the click target when the pod has jobs — "view jobs"
  // stays as the visible affordance, but nobody should have to aim for it.
  const open = hasJobs ? () => onOpenPod(pod.pod) : null;

  return (
    <section
      className={`card pod-card${state === 'incident' ? ' card--alert' : ''}${open ? ' pod-card--link' : ''}`}
      onClick={open || undefined}
      onKeyDown={open ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } } : undefined}
      role={open ? 'button' : undefined}
      tabIndex={open ? 0 : undefined}
      aria-label={open ? `Open ${pod.pod} jobs` : undefined}
    >
      <header className="pod-card__head">
        <h2 className="card__title">{pod.pod}</h2>
        {state === 'incident' && (
          <span className="pod-card__incidents">
            {pod.incident_clusters} active incident{pod.incident_clusters === 1 ? '' : 's'}
          </span>
        )}
      </header>

      {pod.latest.pass_rate !== null ? (
        <p className="pod-card__kpi">
          {pct(pod.latest.pass_rate)}
          <span className="pod-card__kpi-label"> pass · latest runs</span>
          {hasDelta && (
            <span className={`tile__delta${pod.delta_pts >= 0 ? ' is-good' : ' is-bad'}`}>
              {' '}{signedPts(pod.delta_pts)}
            </span>
          )}
        </p>
      ) : (
        // not-harvested ≠ all-green: this pod simply has no runs in the window.
        <p className="pod-card__kpi pod-card__kpi--none">no runs in the window</p>
      )}

      {path ? (
        <svg
          className="pod-card__spark"
          viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`${pod.pod} pass-rate trend over recent runs`}
        >
          <polyline points={path} fill="none" stroke={seriesOne(mode)} strokeWidth="2" />
        </svg>
      ) : (
        <p className="pod-card__nospark">not enough runs to trend</p>
      )}

      <div className="pod-card__chips">
        {chips.length > 0
          ? chips.map((c) => (
            <ClassificationChip key={c.classification} classification={c.classification} count={c.count} mode={mode} />
          ))
          : state === 'quiet' && <span className="chip chip--ok">0 open signals</span>}
      </div>

      {pod.top_signal && (
        <p className="pod-card__teaser" title={`top signal · impact ${pod.top_signal.impact_score}`}>
          <code>{pod.top_signal.title}</code>
        </p>
      )}

      <p className="pod-card__foot">
        {pod.jobs > 0 ? `${compact(pod.jobs)} job${pod.jobs === 1 ? '' : 's'}` : 'no jobs in the listing'}
        {hasJobs && (
          <>
            {' '}·{' '}
            <button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); onOpenPod(pod.pod); }}>
              view jobs →
            </button>
          </>
        )}
      </p>
    </section>
  );
}

export default function PodOverviewScreen({
  jobs,
  regions, region, onRegion, environments, environment, onEnvironment,
  mode, onMode, topbarNav, onOpenPod,
}) {
  const [overview, setOverview] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const fetchSeq = useRef(0);

  // The slice is part of the query (Aug 11 bug: this effect ignored the pills
  // entirely — one global fetch, static cards). Region/environment changes
  // refetch; the fetchSeq guard drops out-of-order responses on fast clicks.
  useEffect(() => {
    const seq = ++fetchSeq.current;
    setError(null);
    setStale(true);
    api.pods({ region, environment })
      .then((o) => { if (seq === fetchSeq.current) setOverview(o); })
      .catch((e) => { if (seq === fetchSeq.current) setError(e.message); })
      .finally(() => { if (seq === fetchSeq.current) setStale(false); });
  }, [retryTick, region, environment]);

  // The matrix is the ONE deliberately-global fetch on this screen
  // (docs/design/multi-region.md): orientation across worlds. Best-effort —
  // a failure hides the matrix, never the pod cards.
  useEffect(() => {
    api.slices().then(setMatrix).catch(() => setMatrix(null));
  }, [retryTick]);

  const podsWithJobs = new Set(jobs.map((j) => j.pod));

  return (
    <div className="app">
      <SharedTopBar
        regions={regions} region={region} onRegion={onRegion}
        environments={environments} environment={environment} onEnvironment={onEnvironment}
        // The API stamps the payload; show ITS clock, not the browser's.
        asOf={overview?.generated_at} refreshing={stale}
        onRefresh={() => setRetryTick((t) => t + 1)}
        mode={mode} onMode={onMode}
        nav={topbarNav}
      />

      <main className={`page${stale ? ' is-stale' : ''}`}>
        <p className="context">
          Pod overview · {String(region || 'us').toUpperCase()}/{environment || 'all'} ·{' '}
          {overview ? `${overview.pods.length} pods · window ${Math.round(overview.window_hours / 24)}d` : '…'}
          {' '}· which pod needs me today?
        </p>

        <SliceMatrix
          matrix={matrix}
          region={region}
          environment={environment}
          onSelectSlice={(r, e) => {
            // One click = one world: both pills move together, everything
            // below refetches for that slice.
            if (r !== region) onRegion(r);
            if (e !== environment) onEnvironment(e);
          }}
        />

        {error && (
          <p className="notice notice--error">
            Couldn't load the pod overview: {error}
            {' '}
            <button type="button" className="ghost-btn" onClick={() => setRetryTick((t) => t + 1)}>
              Retry
            </button>
          </p>
        )}

        {!overview && !error && <p className="notice">Loading pods…</p>}

        {overview && overview.pods.length === 0 && (
          <section className="card empty">
            <h2 className="card__title">No pods in this slice</h2>
            <p className="card__subtitle">
              No jobs or signals in {String(region || 'us').toUpperCase()}/{environment || 'all environments'} for
              this window. Switch region/environment above, or harvest runs from the
              ops panel (/admin) to populate this slice.
            </p>
          </section>
        )}

        {overview && overview.pods.length > 0 && (
          <div className="pod-grid">
            {overview.pods.map((p) => (
              <PodCard
                key={p.pod}
                pod={p}
                mode={mode}
                onOpenPod={onOpenPod}
                hasJobs={podsWithJobs.has(p.pod)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
